import { useEffect, useMemo } from 'react';
import { useMappings } from 'modules/queryBuilder/hooks/queries';
import { getFieldTypeFromFieldMappings } from 'modules/queryBuilder/utils';
import { doesExist } from 'utils/tsHelpers';
import { useIsCurrentConditionId } from './hooks/useIsCurrentConditionId';
import { isEmptyString } from 'utils/stringHelpers';
import { AddButton } from './AddButton';
import { DuplicateButton } from './DuplicateButton';
import { RemoveButton } from './RemoveButton';
import { EventFieldSelect } from './field/EventFieldSelect';
import { ComparatorSelect as ConditionComparatorSelect } from './comparator';
import { useConditionRendererState, useConditionErrorState } from './hooks';
import { CastTypeSelect as ConditionCastTypeSelect } from './value/long/CastTypeSelect';
import { SingleValueInput } from './value/SingleValueInput';
import { MultiValueInput } from './value/MultiValueInput';
import {
  ValidatedMultiStringValueInputProps,
  ValidatedSingleStringValueInputProps,
} from './value/types';
import { ClientDataType, FieldType } from '../../types';
import { useVisualComponentsContext, useConditionStateContext } from '../../contexts';

export type ConditionRendererProps = {
  dataType: ClientDataType;
};

export const ConditionRenderer = ({ dataType }: ConditionRendererProps) => {
  const {
    castType = null,
    comparator = null,
    value = null,
    field = '',
    fieldType = FieldType.UNDEFINED,
    values,
    setValue,
    setCastType,
    setComparator,
    setValues,
    setFieldType,
  } = useConditionStateContext();
  const {
    isMultiValueCondition,
    onFieldChange,
    isCastTypeSelectVisible,
    isComparatorSelectVisible,
    isValueInputVisible,
  } = useConditionRendererState({ dataType });
  const {
    hasFieldError,
    hasFieldTypeError,
    hasComparatorError,
    hasValueError,
    hasValuesError,
    hasFieldWarning,
    hasFieldTypeWarning,
    hasComparatorWarning,
    hasValueWarning,
    hasValuesWarning,
  } = useConditionErrorState();
  const { ConditionLayout } = useVisualComponentsContext();
  const isCurrentConditionId = useIsCurrentConditionId();

  /** if fieldType is missing we get a backup fieldType from mappings based on dataType */
  const { mappings, isLoading } = useMappings({
    dataType,
    isEnabled: fieldType === FieldType.UNDEFINED,
  });
  const backupFieldType = useMemo(
    () => getFieldTypeFromFieldMappings(field, mappings, dataType),
    [field, mappings, dataType],
  );

  useEffect(() => {
    if (
      !isLoading &&
      backupFieldType !== FieldType.UNDEFINED &&
      fieldType === FieldType.UNDEFINED
    ) {
      setFieldType(backupFieldType);
    }
  }, [isLoading, backupFieldType, fieldType]);

  const FieldSelect = (
    <EventFieldSelect
      isOpenByDefault={
        isCurrentConditionId && isEmptyString(field) && !(hasFieldError || hasFieldTypeError)
      }
      dataType={dataType}
      isInvalid={hasFieldError || hasFieldTypeError}
      hasWarning={hasFieldWarning || hasFieldTypeWarning}
      onChange={onFieldChange}
      value={field}
    />
  );

  const ComparatorSelect = (
    <ConditionComparatorSelect
      isOpenByDefault={isCurrentConditionId && !doesExist(comparator) && !hasComparatorError}
      dataType={dataType}
      field={field}
      fieldType={fieldType}
      isInvalid={hasComparatorError}
      hasWarning={hasComparatorWarning}
      onChange={setComparator}
      value={comparator}
    />
  );

  const CastTypeSelect = (
    <ConditionCastTypeSelect
      value={castType}
      onChange={(newCastType) => {
        setCastType(newCastType ?? undefined);
      }}
      dataType={dataType}
      field={field}
      fieldType={fieldType}
    />
  );

  const hasWarning = isMultiValueCondition ? hasValuesWarning : hasValueWarning;
  const hasError = isMultiValueCondition ? hasValuesError : hasValueError;
  const isValueOpenByDefault =
    isCurrentConditionId &&
    !doesExist(isMultiValueCondition ? values : value) &&
    doesExist(comparator) &&
    !hasWarning &&
    !hasError;

  const valueInputProps = {
    comparator,
    dataType,
    field,
    fieldType,
    castType,
    isOpenByDefault: isValueOpenByDefault,
    hasWarning,
    isInvalid: hasError,
    onChange: isMultiValueCondition ? setValues : setValue,
    value: isMultiValueCondition ? values : value,
  };

  const ValueInput = isMultiValueCondition ? (
    <MultiValueInput {...(valueInputProps as ValidatedMultiStringValueInputProps)} />
  ) : (
    <SingleValueInput {...(valueInputProps as ValidatedSingleStringValueInputProps)} />
  );

  return (
    <ConditionLayout
      AddCondition={<AddButton />}
      FieldSelect={FieldSelect}
      CastTypeSelect={isCastTypeSelectVisible && CastTypeSelect}
      ComparatorSelect={isComparatorSelectVisible && ComparatorSelect}
      ValueInput={isValueInputVisible && ValueInput}
      Delete={<RemoveButton />}
      Duplicate={<DuplicateButton />}
    />
  );
};
